import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { coursePartner, courseAudienceLabels, courseAudienceValues, courses, coursesFor } from '../../shared/courses';
import type { Course, CourseAudience } from '../../shared/courses';
import './courses.css';

/** Partner logo and one line: who teaches and that the request comes to us. */
export function CoursePartnerNote() {
  return <div className="course-partner">
    <a className="course-partner-logo" href={coursePartner.url} target="_blank" rel="noreferrer"><img src={coursePartner.logo} alt={`Школа «${coursePartner.name}»`} width={1448} height={1048} loading="lazy" decoding="async" /></a>
    <p>Курсы и лекции проводит школа <a href={coursePartner.url} target="_blank" rel="noreferrer">«{coursePartner.name}»</a> — первая школа операторов БПЛА в Краснодарском крае. Заявку на курс оставляете у нас, как и на экскурсию.</p>
  </div>;
}

/** Volume and starting price. */
export function CourseFacts({ course }: { course: Course }) {
  const details = courses[course];
  return <dl className="course-facts">
    <div><dt>Объём</dt><dd>{details.volume}</dd></div>
    <div><dt>Стоимость</dt><dd>{details.price}</dd></div>
  </dl>;
}

/** Full programme: what is learnt, how, the result, hours and the document. `level` is the section heading level. */
export function CourseDetails({ course, level }: { course: Course; level: 3 | 4 }) {
  const details = courses[course];
  const Heading = level === 3 ? 'h3' : 'h4';
  return <div className="course-details">
    <section><Heading>Чему научитесь</Heading><ul>{details.learn.map((item) => <li key={item}>{item}</li>)}</ul></section>
    <section><Heading>Как проходят занятия</Heading><p>{details.how}</p></section>
    <section><Heading>Итог курса</Heading><p>{details.result}</p></section>
    <section><Heading>Часы</Heading><ul className="course-hours">{details.hours.map((item) => <li key={item}>{item}</li>)}</ul></section>
    <section><Heading>Документ</Heading><p>{details.certificate}</p></section>
    <p className="course-official">Программа в документах школы: «{details.officialName}»</p>
  </div>;
}

/**
 * One audience at a time («Детям и подросткам» / «Взрослым») in a horizontal snap rail: arrows on mouse screens,
 * swipe on touch. Both rails stay in the markup, the other one hidden. A card opens the course details
 * (`onOpen` gets the clicked button for focus return).
 */
export function CourseRail({ onOpen }: { onOpen: (course: Course, source: HTMLButtonElement) => void }) {
  const id = useId();
  const [audience, setAudience] = useState<CourseAudience>('kids');
  // Server render and hydration show the start of the rail: back is off until the rail scrolls.
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const rails = useRef<Partial<Record<CourseAudience, HTMLUListElement | null>>>({});
  const tablist = useRef<HTMLDivElement>(null);

  function updateEdges() {
    const rail = rails.current[audience];
    if (!rail) return;
    setAtStart(rail.scrollLeft <= 2);
    setAtEnd(rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 2);
  }

  // Re-measure when the audience switches (the other rail keeps its own scroll) and whenever the rail changes size:
  // window resizes and the parent tab panel turning visible (a hidden rail measures zero).
  useEffect(() => {
    const rail = rails.current[audience];
    if (!rail) return;
    const observer = new ResizeObserver(updateEdges);
    observer.observe(rail);
    return () => observer.disconnect();
  }, [audience]);

  function scrollRail(direction: -1 | 1) {
    const rail = rails.current[audience];
    if (!rail || (direction < 0 ? atStart : atEnd)) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    rail.scrollBy({ left: direction * rail.clientWidth, behavior: reduced ? 'instant' : 'smooth' });
  }

  /** Arrow keys, Home and End move between the audiences and select them. */
  function audienceKeys(event: KeyboardEvent<HTMLDivElement>) {
    const current = courseAudienceValues.indexOf(audience);
    const targets: Partial<Record<string, number>> = { ArrowLeft: current - 1, ArrowRight: current + 1, Home: 0, End: courseAudienceValues.length - 1 };
    const target = targets[event.key];
    if (target === undefined) return;
    event.preventDefault();
    const next = courseAudienceValues[(target + courseAudienceValues.length) % courseAudienceValues.length]!;
    setAudience(next);
    tablist.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[courseAudienceValues.indexOf(next)]?.focus();
  }

  return <div className="course-showcase">
    <div className="course-showcase-bar">
      <div className="course-audience-tabs" role="tablist" aria-label="Для кого курсы" ref={tablist} onKeyDown={audienceKeys}>
        {courseAudienceValues.map((item) => <button key={item} id={`${id}-${item}-tab`} className="course-audience-tab" type="button" role="tab" aria-selected={audience === item} aria-controls={`${id}-${item}`} tabIndex={audience === item ? 0 : -1} onClick={() => setAudience(item)}>{courseAudienceLabels[item]}</button>)}
      </div>
      <div className="course-rail-arrows">
        <button type="button" aria-label="Предыдущие курсы" aria-controls={`${id}-${audience}`} aria-disabled={atStart} onClick={() => scrollRail(-1)}><span aria-hidden="true">←</span></button>
        <button type="button" aria-label="Следующие курсы" aria-controls={`${id}-${audience}`} aria-disabled={atEnd} onClick={() => scrollRail(1)}><span aria-hidden="true">→</span></button>
      </div>
    </div>
    {courseAudienceValues.map((item) => <div key={item} id={`${id}-${item}`} role="tabpanel" aria-labelledby={`${id}-${item}-tab`} hidden={audience !== item}>
      <ul className="course-rail" data-swipe-own="" ref={(element) => { rails.current[item] = element; }} onScroll={audience === item ? updateEdges : undefined}>
        {coursesFor(item).map((course) => <li className="course-card" key={course}>
          <h4><button type="button" aria-haspopup="dialog" onClick={(event) => onOpen(course, event.currentTarget)}>{courses[course].title}</button></h4>
          <p className="course-card-lead">{courses[course].lead}</p>
          <p className="course-card-meta"><span>{courses[course].volume}</span><strong>{courses[course].price}</strong></p>
        </li>)}
      </ul>
    </div>)}
  </div>;
}
